<template>
  <Modal
    :data-testid="$attrs['data-testid'] || null"
    :scrollable-content="false"
    :show="opened"
    :style="modalStyles"
    class="scm-Modal"
    manual-close
    size="lg-next"
    @close="onClose"
    @after-enter="focusNameInput"
    @before-enter="onBeforeEnter"
  >
    <template #header>
      <div class="scm-TitleWrapper">
        <SkeletonItem
          v-if="isScheduleDataLoading || !frequencyGeneralData"
          height="28px"
          width="100%"
        />

        <template v-else>
          <AppInput
            :ref="nameInputKey"
            v-model="frequencyGeneralData[NAME]"
            :data-auto-testid="NAME_FIELD_TEST_ID"
            :data-testid="NAME_FIELD_TEST_ID"
            :is-error="!isNameValid"
            :max-length="SCHEDULE_NAME_MAX_LENGTH"
            :placeholder="t('reminder_schedules.name.placeholder')"
            class="scm-Title"
            show-error-for-placeholder
            style-type="title"
            @focus="isNameValid = true"
          />
          <AppFieldError v-if="!isNameValid" :show="!isNameValid" class="scm-TitleError">
            {{ t('field.required') }}
          </AppFieldError>
        </template>
      </div>
    </template>

    <template #before-close>
      <SavingIndicator :ref="savingIndicatorKey" />
    </template>

    <template #loader>
      <SavingIndicator :ref="savingIndicatorTopKey" :type="SAVING_INDICATOR_TYPES.LINE" />
    </template>

    <div class="scm-Body">
      <AppRadioGroup
        :disabled="isScheduleDataLoading"
        :model-value="currentTab"
        :options="Object.values(TABS)"
        :type="RADIO_GROUP_TYPES.TAB_LIKE"
        name="manage-schedule-tabs"
        @update:model-value="onTabChange"
      />

      <template v-if="currentTab === TABS.FREQUENCY.value">
        <ScheduleModalLoader v-if="isScheduleDataLoading" />

        <template v-else>
          <ScheduleFrequencyManagement
            v-if="frequencyGeneralData && frequencyParams"
            v-model:frequency-general-data="frequencyGeneralData"
            v-model:frequency-params="frequencyParams"
          />

          <FormCaption
            v-if="!isEmpty(scheduleReminders)"
            :subtitle="t('schedule.reminders.subtitle')"
            :title="t('schedule.reminders.title')"
          >
            <ScheduleReminderItem
              v-for="(reminder, index) in scheduleReminders"
              :key="reminder[REMINDER_ENTITY_KEYS.ID]"
              v-model="scheduleReminders[index]"
            />
          </FormCaption>
        </template>
      </template>

      <ScheduleParticipantsManagement
        v-else-if="currentTab === TABS.PARTICIPANTS.value"
        v-model="scheduleParticipants"
        v-model:is-some-dropdown-opened="isSomeParticipantsDropdownOpen"
        :validation-errors="validationErrors"
        @remove-error="removeError"
      />

      <ScheduleNotificationsManagement
        v-else-if="currentTab === TABS.NOTIFICATIONS.value"
        v-model:channels-attributes="scheduleNotifications.channelsAttributes"
        v-model:selected-channels="scheduleNotifications.selectedChannels"
      />
    </div>

    <template #footer>
      <div class="scm-Footer">
        <AppDivider class="scm-Footer_Divider" no-margin />
        <ModalFooterActions
          :disabled="isConfirmButtonDisabled"
          :loading="isSaving || isScheduleDataLoading"
          hide-checkbox
          @close="onClose"
          @create="onConfirm"
        >
          <template #confirm-button-text> {{ confirmButtonText }}</template>
        </ModalFooterActions>
      </div>
    </template>
  </Modal>

  <portal to="modal-windows">
    <AppDialog
      :show="isConfirmCloseShow"
      :title="$t('confirm_modal.title')"
      :type="DIALOG_TYPES.WARNING"
      @on-close="hideConfirmCloseModal"
      @on-confirm="onConfirmClose"
    >
      {{ $t('confirm_modal.description') }}

      <template #confirm-btn-text>
        {{ $t('confirm.discard_btn') }}
      </template>
    </AppDialog>
  </portal>
</template>

<script setup>
import { cloneDeep, isEmpty, isObject } from 'lodash'
import { computed, ref, toValue, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import AlertsApiHandler from '@/api/alerts'
import SchedulesApiHandler from '@/api/schedules'
import { ALERTS_EXCLUDED_CHANNELS } from '@/utils/check-ins/alerts-helpers'
import {
  useTriggersModal,
  useParticipantsValidation,
  useScheduleDateCalculation,
  TRIGGER_MODAL_ENTITY_TYPES
} from '@/utils/check-ins/schedule-composables'
import { useScheduleModalTabs } from '@/utils/check-ins/schedule-ui-helpers'
import {
  createNotificationsPayload,
  createRemindersPayload,
  DEFAULT_FORM_MODEL,
  DEFAULT_REMINDERS,
  FREQUENCY_INNER_PARAMS,
  createScheduleParamsPayload,
  parsePayloadToFormModel,
  SCHEDULE_NAME_MAX_LENGTH,
  getDefaultNotificationsModel,
  getResolvedNotificationsChannels
} from '@/utils/check-ins/schedules-helpers'
import {
  createParticipantsPayload,
  DEFAULT_SCHEDULE_PARTICIPANTS
} from '@/utils/check-ins/schedules-participants'
import { DIALOG_TYPES } from '@/utils/components-configurations/app-dialog'
import { RADIO_GROUP_TYPES } from '@/utils/components-configurations/app-radio-group'
import { SAVING_INDICATOR_TYPES } from '@/utils/components-configurations/saving-indicator'
import {
  NOTIFICATION_CHANNEL_ENTITY_KEYS,
  REMINDER_ENTITY_KEYS,
  SCHEDULE_ENTITY_KEYS
} from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { numberOrNullProp } from '@/utils/prop-validators'

import AppDialog from '@/components/AppDialog'
import FormCaption from '@/components/check-ins/FormCaption'
import { NAME_FIELD_TEST_ID } from '@/components/check-ins/jest-helpers'
import ScheduleFrequencyManagement from '@/components/check-ins/ScheduleFrequencyManagement'
import ScheduleNotificationsManagement from '@/components/check-ins/ScheduleNotificationsManagement'
import ScheduleParticipantsManagement from '@/components/check-ins/ScheduleParticipantsManagement'
import ScheduleReminderItem from '@/components/check-ins/ScheduleReminderItem'
import AppFieldError from '@/components/form/AppFieldError'
import ModalFooterActions from '@/components/objectives/forms/ModalFooterActions'
import SavingIndicator from '@/components/SavingIndicator'
import AppDivider from '@/components/ui/AppDivider'
import AppInput from '@/components/ui/AppInput/AppInput'
import AppRadioGroup from '@/components/ui/AppRadioGroup/AppRadioGroup'
import Modal from '@/components/ui/Modal/Modal'
import ScheduleModalLoader from '@/components/ui/SkeletonLoaders/ScheduleModalLoader'
import SkeletonItem from '@/components/ui/SkeletonLoaders/SkeletonItem'

defineOptions({
  name: 'ScheduleModal'
})

const { NAME, ID } = SCHEDULE_ENTITY_KEYS

const props = defineProps({
  opened: {
    type: Boolean
  },

  scheduleId: {
    required: true,
    validator: v => numberOrNullProp(v)
  }
})

const { t } = useI18n()

const { TABS } = useScheduleModalTabs()

const currentTab = ref(TABS.FREQUENCY.value)

const onTabChange = value => {
  currentTab.value = value
}

const frequencyGeneralData = ref(null)
const frequencyParams = ref(null)
const scheduleReminders = ref([])
const scheduleNotifications = ref({})
const scheduleParticipants = ref(null)

const emit = defineEmits({
  'on-close': null,
  'reload-schedules': null,
  'create-alert-from-reference': v => isObject(v) && !isEmpty(v)
})

watch(
  () => props.opened,
  newValue => {
    if (!newValue) {
      frequencyGeneralData.value = null
      frequencyParams.value = null
      scheduleReminders.value = []
      scheduleNotifications.value = {}
      currentTab.value = TABS.FREQUENCY.value
      isNameValid.value = true
      schedulePayloadSnapshot.value = null
      isCreateAlertAllowed.value = true
    }
  }
)

const applyEditState = async () => {
  const api = new SchedulesApiHandler()

  try {
    isScheduleDataLoading.value = true

    const scheduleData = await api.getScheduleById({ id: props.scheduleId })

    setFormModels({ scheduleData })
  } catch (error) {
    handleError({ error })
  } finally {
    isScheduleDataLoading.value = false
  }
}

const setFormModels = ({ scheduleData = {} } = {}) => {
  schedulePayloadSnapshot.value = cloneDeep({ ...scheduleData })

  const { generalData, frequencyParamsData, remindersData, notificationsData, participantsData } =
    parsePayloadToFormModel({
      payload: scheduleData
    })

  frequencyGeneralData.value = cloneDeep(generalData)
  frequencyParams.value = cloneDeep(frequencyParamsData)
  scheduleReminders.value = cloneDeep(remindersData)
  scheduleNotifications.value = cloneDeep(notificationsData)
  scheduleParticipants.value = cloneDeep(participantsData)
  resetValidationErrors()
}

const DEFAULT_NOTIFICATIONS = getDefaultNotificationsModel()

const applyCreateState = () => {
  frequencyGeneralData.value = cloneDeep(DEFAULT_FORM_MODEL)
  frequencyParams.value = cloneDeep(FREQUENCY_INNER_PARAMS)
  scheduleReminders.value = cloneDeep(DEFAULT_REMINDERS)
  scheduleNotifications.value = cloneDeep(DEFAULT_NOTIFICATIONS)
  scheduleParticipants.value = cloneDeep(DEFAULT_SCHEDULE_PARTICIPANTS)
  resetValidationErrors()
}

const onBeforeEnter = () => {
  if (isEdit.value) {
    applyEditState()
  } else {
    applyCreateState()
  }
}

const confirmButtonText = computed(() => {
  if (isEdit.value) {
    return t('action.save')
  }

  if (toValue(nextTab)) {
    return t('schedule.next_step', {
      stepName: t('common.select_entity', { entity: toValue(nextTab).label }).toLowerCase()
    })
  }

  return t('action.create')
})

const { selectedDateDayNumber, humanReadableRelativeDate } = useScheduleDateCalculation({
  formModel: frequencyGeneralData
})

const getFullPayload = () => {
  const frequencyDataPayload = createScheduleParamsPayload({
    scheduleData: toValue(frequencyGeneralData),
    frequencyParams: toValue(frequencyParams),
    selectedDateDayNumber: toValue(selectedDateDayNumber),
    nthDayNumber: humanReadableRelativeDate.value.nthDayNumber
  })

  return {
    ...frequencyDataPayload,
    [SCHEDULE_ENTITY_KEYS.REMINDERS]: createRemindersPayload({
      reminders: toValue(scheduleReminders)
    }),
    [SCHEDULE_ENTITY_KEYS.NOTIFICATION_CHANNEL_IDS]: createNotificationsPayload({
      ...toValue(scheduleNotifications)
    }),
    [SCHEDULE_ENTITY_KEYS.PARTICIPANTS]: createParticipantsPayload({
      participants: toValue(scheduleParticipants)
    })
  }
}

const sendCloseEvent = () => emit('on-close')

const {
  isSomeParticipantsDropdownOpen,
  modalStyles,
  payloadSnapshot: schedulePayloadSnapshot,
  isEdit,
  showSuccessNotification,
  nameInputKey,
  focusNameInput,
  isSaving,
  closeModalOnSavingFinished,
  isConfirmCloseShow,
  hideConfirmCloseModal,
  onConfirmClose,
  onClose,
  savingIndicatorKey,
  savingIndicatorTopKey,
  onSavingStarted,
  onSavingFinished,
  isNameValid,
  validateName,
  isConfirmButtonDisabled,
  isDataLoading: isScheduleDataLoading
} = useTriggersModal({
  opened: () => props.opened,
  payload: () => getFullPayload(),
  checkDataChangeExcludedKeys: [ID],
  entityId: () => props.scheduleId,
  entityType: TRIGGER_MODAL_ENTITY_TYPES.CHECKIN_SCHEDULE,
  sendCloseEvent
})

//#region CREATE ALERT SCOPE

const isCreateAlertAllowed = ref(true)

const availableChannelsForAlerts = computed(() => {
  return Object.values(getResolvedNotificationsChannels(ALERTS_EXCLUDED_CHANNELS) || {}).map(
    ch => ch[NOTIFICATION_CHANNEL_ENTITY_KEYS.VALUE]
  )
})

const shouldProposeAlertCreation = computed(() => {
  if (isEdit.value) return false

  return (scheduleNotifications.value.selectedChannels || []).some(channelId =>
    availableChannelsForAlerts.value.includes(channelId)
  )
})

const alertsApi = new AlertsApiHandler()

watch(shouldProposeAlertCreation, async v => {
  if (v) {
    try {
      isCreateAlertAllowed.value = await alertsApi.checkIsCreateAlertAllowed()
    } catch (error) {
      handleError({ error })
      isCreateAlertAllowed.value = false
    }
  }
})

//#endregion CREATE ALERT SCOPE

watch(
  frequencyParams,
  (newValue, oldValue) => {
    if (!isEdit.value && oldValue && newValue && !schedulePayloadSnapshot.value) {
      schedulePayloadSnapshot.value = {
        ...cloneDeep(getFullPayload())
      }
    }
  },
  { deep: true, immediate: true }
)

const nextTab = computed(() => {
  const indexOfSelectedTab = Object.values(TABS).findIndex(tab => tab.value === currentTab.value)

  return Object.values(TABS)[indexOfSelectedTab + 1]
})

const {
  validate: validateParticipants,
  validationErrors,
  resetValidationErrors,
  removeError
} = useParticipantsValidation({
  formModel: scheduleParticipants
})

const shouldInvokeAlertCreation = computed(() => {
  return !isEdit.value && isCreateAlertAllowed.value && shouldProposeAlertCreation.value
})

const onConfirm = async () => {
  if (isConfirmButtonDisabled.value) return

  if (!isEdit.value && toValue(nextTab)) {
    onTabChange(toValue(nextTab).value)
    return
  }

  validateName(frequencyGeneralData.value[NAME])

  if (!isNameValid.value) {
    frequencyGeneralData.value[NAME] = ''
    return
  }

  const isParticipantsValid = validateParticipants()

  if (!isParticipantsValid) {
    if (currentTab.value !== TABS.PARTICIPANTS.value) {
      onTabChange(TABS.PARTICIPANTS.value)
    }

    return
  }

  const api = new SchedulesApiHandler()

  try {
    closeModalOnSavingFinished.value = !isEdit.value

    onSavingStarted()

    const requestMethod = isEdit.value
      ? payload => api.updateSchedule({ id: props.scheduleId, ...payload })
      : payload => api.createSchedule(payload)

    const payload = getFullPayload()

    const schedule = await requestMethod(payload)

    if (isEdit.value) {
      setFormModels({ scheduleData: schedule })
    }

    showSuccessNotification(payload[NAME])

    emit('reload-schedules', {
      eventName: isEdit.value ? 'notification schedule updated' : 'notification schedule created',
      ...payload
    })

    if (shouldInvokeAlertCreation.value) {
      emit('create-alert-from-reference', schedule)
    }
  } catch (error) {
    closeModalOnSavingFinished.value = false
    handleError({ error })
  } finally {
    onSavingFinished()
  }
}
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/schedule-checkin-modal';

.scm-Modal {
  @extend %modal-styles;
}

.scm-Body {
  @extend %modal-body-styles;
}

.scm-Footer {
  @extend %modal-footer-styles;
}

.scm-Footer_Divider {
  @extend %modal-footer-divider-styles;
}

.scm-TitleWrapper {
  @extend %modal-title-wrapper-styles;
}

.scm-Title {
  @extend %modal-input-title-styles;
}

.scm-TitleError {
  @extend %modal-input-title-error-styles;
}
</style>
